import type { UserPreview } from './api';

export function publicProfilePath(user: { id: number; nickname?: string | null }): string {
  return user.nickname?.trim() ? `/${encodeURIComponent(user.nickname.trim())}` : `/users/${user.id}`;
}

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? Array.from(new Set(value.map(text).filter(Boolean))) : [];
}

export function publicProfileUrl(value: unknown): string | undefined {
  const candidate = text(value);
  if (!candidate || /[\u0000-\u0020\u007f]/.test(candidate)) return undefined;
  try {
    const url = new URL(candidate);
    return (url.protocol === 'https:' || url.protocol === 'http:') && !url.username && !url.password ? url.href : undefined;
  } catch {
    return undefined;
  }
}

export function publicProfileDetails(profile: UserPreview) {
  // Сервер владеет разрешениями: нормализуем только его UserPreview, не AppointmentOwner.
  const resume = record(profile.is_profile_masked ? null : profile.resume_data);
  const portfolio = record(profile.is_profile_masked ? null : profile.portfolio_data);
  const entries = (value: unknown) => Array.isArray(value) ? value.map(record) : [];
  return {
    headline: text(resume.headline),
    summary: text(resume.summary),
    fullText: text(resume.full_text),
    experience: entries(resume.experience).map((item) => ({
      company: text(item.company), position: text(item.position), period: text(item.period), description: text(item.description),
    })).filter((item) => item.company || item.position || item.description),
    publications: entries(resume.publications).map((item) => ({
      title: text(item.title), description: text(item.description), url: publicProfileUrl(item.url),
    })).filter((item) => item.title || item.description || item.url),
    projects: entries(resume.projects).map((item) => ({
      name: text(item.name), role: text(item.role), description: text(item.description), url: publicProfileUrl(item.url),
    })).filter((item) => item.name || item.description || item.url),
    skills: strings(portfolio.skills),
    links: Array.from(new Set(strings(portfolio.links).map(publicProfileUrl).filter((url): url is string => Boolean(url)))),
    cases: strings(portfolio.projects),
  };
}

export function profileScheduleVisible(profile: UserPreview): boolean {
  const visibility = profile.profile_visibility ?? (profile.is_profile_public === false ? 0 : 1);
  return !profile.is_profile_masked && visibility >= 1;
}

export type PublicProfilePresentation = {
  version: 1;
  topics: Array<{ id: string; label: string; description: string }>;
  stories: Array<{
    id: string; project_id?: number; title: string; task: string; contribution: string; result: string;
    images: Array<{ url: string; caption: string }>;
    materials: Array<{ label: string; url: string }>;
    topic_ids: string[];
  }>;
  publication_topics: Record<string, string[]>;
  achievements: Array<{
    id: string; date: string; title: string; description: string; contribution: string;
    evidence_url?: string; project_id?: number; publication_key?: string; topic_ids: string[];
  }>;
  focus?: { text: string; cooperation: string; expires_at: string; project_id?: number; topic_id?: string };
  greeting?: { video_url: string; captions_vtt: string };
};

export function publicationKey(value: unknown): string {
  const item = record(value);
  const url = publicProfileUrl(item.url);
  return url ? `url:${url}` : (text(item.title) ? `title:${text(item.title).replace(/\s+/g, ' ')}` : '');
}

export function publicProfileCaptionsValid(value: unknown): value is string {
  if (typeof value !== 'string' || new TextEncoder().encode(value).length > 30000) return false;
  const blocks = value.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n').trim().split(/\n[\t ]*\n/);
  const header = blocks.shift() || '';
  if (!/^WEBVTT(?:[\t ][^\n]*)?$/.test(header) || header.includes('-->') || /[<>\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(header)) return false;
  if (!blocks.length || blocks.length > 200) return false;
  const time = (part: string) => {
    const match = /^(?:(\d{2,}):)?([0-5]\d):([0-5]\d)\.(\d{3})$/.exec(part);
    return match ? Number(match[1] || 0) * 3600000 + Number(match[2]) * 60000 + Number(match[3]) * 1000 + Number(match[4]) : NaN;
  };
  return blocks.every((block) => {
    const lines = block.split('\n');
    if (!lines[0].includes('-->') && /[<>\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(lines.shift() || '')) return false;
    const match = /^(\S+) --> (\S+)$/.exec(lines.shift() || '');
    return Boolean(match && time(match[2]) > time(match[1]) && lines.some((line) => line.trim())
      && !/[<>\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(lines.join('\n'))
      && !/^(STYLE|REGION|NOTE)(?:\s|$)/.test(block));
  });
}

export function normalizePublicProfilePresentation(value: unknown, resumePublications?: unknown): PublicProfilePresentation {
  const source = record(value);
  const empty: PublicProfilePresentation = { version: 1, topics: [], stories: [], publication_topics: {}, achievements: [] };
  if (source.version !== 1) return empty;
  const bounded = (value: unknown, max: number) => typeof value === 'string' && Array.from(value.trim()).length <= max
    && !/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(value) ? value.trim() : '';
  const id = (value: unknown) => typeof value === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(value) ? value : '';
  const url = (value: unknown) => typeof value === 'string' && value.length <= 2048 ? publicProfileUrl(value) : undefined;
  const projectId = (value: unknown) => typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? value : undefined;
  const entries = (value: unknown, max: number) => Array.isArray(value) ? value.slice(0, max).map(record) : [];
  const unique = <T extends { id: string }>(values: T[]) => values.filter((item, index) => item.id && values.findIndex((other) => other.id === item.id) === index);
  const topics = unique(entries(source.topics, 3).map((item) => ({
    id: id(item.id), label: bounded(item.label, 48), description: bounded(item.description, 280),
  }))).filter((item) => item.label);
  const topicIds = (value: unknown) => strings(value).filter((value) => topics.some((topic) => topic.id === value)).slice(0, 3);
  const publications = new Set((Array.isArray(resumePublications) ? resumePublications : []).map(publicationKey).filter(Boolean));
  const stories = unique(entries(source.stories, 12).filter((item) => item.project_id === undefined || projectId(item.project_id)).map((item) => ({
    id: id(item.id), ...(projectId(item.project_id) ? { project_id: projectId(item.project_id) } : {}),
    title: bounded(item.title, 160), task: bounded(item.task, 2000), contribution: bounded(item.contribution, 2000), result: bounded(item.result, 2000),
    images: entries(item.images, 4).flatMap((image) => { const href = url(image.url); return href ? [{ url: href, caption: bounded(image.caption, 200) }] : []; }),
    materials: entries(item.materials, 6).flatMap((material) => { const href = url(material.url); const label = bounded(material.label, 100); return href && label ? [{ url: href, label }] : []; }),
    topic_ids: topicIds(item.topic_ids),
  }))).filter((item) => item.title);
  const publication_topics: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(record(source.publication_topics)).slice(0, 50)) {
    if (publications.has(key)) publication_topics[key] = topicIds(value);
  }
  const dateValid = (value: unknown) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value;
  const achievements = unique(entries(source.achievements, 30).filter((item) => dateValid(item.date)
    && (item.project_id === undefined || projectId(item.project_id))
    && (item.publication_key === undefined || publications.has(text(item.publication_key))))
    .map((item) => ({
      id: id(item.id), date: String(item.date), title: bounded(item.title, 160), description: bounded(item.description, 2000), contribution: bounded(item.contribution, 1200),
      ...(url(item.evidence_url) ? { evidence_url: url(item.evidence_url) } : {}),
      ...(projectId(item.project_id) ? { project_id: projectId(item.project_id) } : {}),
      ...(text(item.publication_key) ? { publication_key: text(item.publication_key) } : {}), topic_ids: topicIds(item.topic_ids),
    }))).filter((item) => item.title);
  const result: PublicProfilePresentation = { version: 1, topics, stories, publication_topics, achievements };
  const focus = record(source.focus);
  const expires = text(focus.expires_at);
  if (bounded(focus.text, 280) && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(expires)
    && Number.isFinite(Date.parse(expires)) && new Date(expires).toISOString().replace('.000Z', 'Z') === expires.replace('.000Z', 'Z')
    && (focus.project_id === undefined || projectId(focus.project_id))) {
    result.focus = { text: bounded(focus.text, 280), cooperation: bounded(focus.cooperation, 200), expires_at: expires,
      ...(projectId(focus.project_id) ? { project_id: projectId(focus.project_id) } : {}),
      ...(topics.some((topic) => topic.id === focus.topic_id) ? { topic_id: text(focus.topic_id) } : {}),
    };
  }
  const greeting = record(source.greeting);
  const video = url(greeting.video_url);
  if (video && publicProfileCaptionsValid(greeting.captions_vtt)) result.greeting = { video_url: video, captions_vtt: greeting.captions_vtt };
  return result;
}

export function publicProfilePresentation(profile: UserPreview, now: number | Date = Date.now()): PublicProfilePresentation {
  const resume = record(profile.is_profile_masked ? null : profile.resume_data);
  const result = normalizePublicProfilePresentation(resume.public_profile, resume.publications);
  if (result.focus && Date.parse(result.focus.expires_at) <= Number(now)) delete result.focus;
  return result;
}
